import Column from '@app/components/layout/Column'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { HeaderTabs } from '@app/components/core/tabs/HeaderTabs'
import { match } from 'ts-pattern'
import { PageLayout } from '../layout/PageLayout'
import { pages } from '@app/util/links'
import { useLocation } from 'wouter'
import { DiscoverHousingForms } from './housing-forms/DiscoverHousingForms'
import { DiscoverRegions } from './regions/DiscoverRegions'
import { DiscoverInitiatives } from './initiatives/DiscoverInitiatives'
import { DiscoverPlots } from './plots/DiscoverPlots'
import { useParamSearch, useParamState } from '@app/util/hooks/useParamState'
import { SearchBar } from '@app/components/blocks/searchBar/SearchBar'
import useMediaQuery from '@app/util/useMediaQuery'
import useTransform from '@app/util/useTransform'
import { useLocationSuggestions } from '@app/components/blocks/searchBar/useLocationSuggestions'
import { SuggestionsFetcher } from '@app/components/blocks/searchBar/SearchField'
import InitiativeDefIcon from '@app/assets/discovery/initiativen-default.svg'
import LocationDefIcon from '@app/assets/discovery/location-default.svg'
import RegionDefIcon from '@app/assets/discovery/regio-default.svg'
import HousingFormDefIcon from '@app/assets/discovery/woonvorm-default.svg'
import InitiativeActiveIcon from '@app/assets/discovery/initiativen-active-hover.svg'
import LocationActiveIcon from '@app/assets/discovery/location-active-hover.svg'
import RegionActiveIcon from '@app/assets/discovery/regio-active-hover.svg'
import HousingFormActiveIcon from '@app/assets/discovery/woonvorm-active-hover.svg'
import { PageSeo } from '@app/components/core/seo/PageSeo'

const tabs = {
  initiatives: 'initiatives',
  locations: 'locations',
  regions: 'regions',
  housingForms: 'housingForms',
} as const
type Tabs = keyof typeof tabs

const views = {
  grid: 'grid',
  map: 'map',
} as const

export const Discover = ({ tab = tabs.initiatives }: { tab?: Tabs }) => {
  const [_location, navigate] = useLocation()
  const isMobile = useMediaQuery()
  const transformStyle = useTransform('center')
  const [language, _setLanguage] = useLanguage()
  const translations = i18n.languages[language].app.discoveryPage
  const [searchTerm, setSearchTerm] = useParamSearch()
  const [searchBarInput, setSearchBarInput] = useState(searchTerm)
  const [view, setValue, toggleValue] = useParamState(views, 'view')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const locationSuggestions = useLocationSuggestions()

  const hasMapAndFilters = tab === tabs.initiatives || tab === tabs.locations

  useEffect(() => {
    setSearchBarInput(searchTerm)
  }, [searchTerm])

  const changeTab = (page: string) => {
    onResetSearch()
    navigate(page)
  }

  const onResetSearch = useCallback(() => {
    setSearchTerm('')
  }, [setSearchTerm])

  const onFiltersToggle = useCallback(() => {
    setFiltersOpen((prev) => !prev)
  }, [])

  const onFiltersClose = useCallback(() => {
    setFiltersOpen(false)
  }, [])

  useEffect(() => {
    if (isMobile) {
      setValue(views.grid)
    }
  }, [isMobile, setValue])

  const placeholder = isMobile
    ? translations.searchPlaceholder.mobile
    : match(tab)
        .with(
          tabs.initiatives,
          () => translations.searchPlaceholder.initiatives,
        )
        .with(tabs.locations, () => translations.searchPlaceholder.plots)
        .with(tabs.regions, () => translations.searchPlaceholder.regions)
        .with(
          tabs.housingForms,
          () => translations.searchPlaceholder.housingForms,
        )
        .exhaustive()

  const suggestionsFetcher = match(tab)
    .with(tabs.initiatives, () => locationSuggestions)
    .with(tabs.locations, (): SuggestionsFetcher => locationSuggestions)
    .with(tabs.regions, (): SuggestionsFetcher => locationSuggestions)
    .otherwise((): SuggestionsFetcher | undefined => undefined)

  const meta = useMemo(
    () =>
      match(tab)
        .with(tabs.initiatives, () => ({
          title: translations.tabs.initiatives.title,
          description: translations.tabs.initiatives.subTitle,
        }))
        .with(tabs.locations, () => ({
          title: translations.tabs.plots.title,
          description: translations.tabs.plots.subTitle,
        }))
        .with(tabs.regions, () => ({
          title: translations.tabs.regions.provinceTitle,
          description: translations.tabs.regions.provinceSubTitle,
        }))
        .with(tabs.housingForms, () => ({
          title: translations.tabs.housingForms.title,
          description: translations.tabs.housingForms.subtitle,
        }))
        .exhaustive(),
    [tab, translations],
  )

  return (
    <PageLayout gap="2rem">
      <PageSeo pageTitle={meta.title} description={meta.description} />

      <Column
        alignItems="center"
        gap={isMobile ? '1.5rem' : '2.5rem'}
        style={{
          alignSelf: 'center',
          ...transformStyle,
          zIndex: 3,
          flexGrow: 0,
        }}
      >
        <HeaderTabs.Group
          selectedTab={tab}
          style={{ gap: isMobile ? '1rem' : '2.5rem', ...transformStyle }}
        >
          <HeaderTabs.Tab
            icon={InitiativeDefIcon}
            activeIcon={InitiativeActiveIcon}
            label={translations.initiatives}
            value={tabs.initiatives}
            href={pages.initiatives}
            onSelectTab={() => changeTab(pages.initiatives)}
          />
          <HeaderTabs.Tab
            icon={LocationDefIcon}
            activeIcon={LocationActiveIcon}
            label={translations.plots}
            value={tabs.locations}
            href={pages.plots}
            onSelectTab={() => changeTab(pages.plots)}
          />
          <HeaderTabs.Tab
            icon={RegionDefIcon}
            activeIcon={RegionActiveIcon}
            label={translations.regions}
            value={tabs.regions}
            href={pages.regions}
            onSelectTab={() => changeTab(pages.regions)}
          />
          <HeaderTabs.Tab
            icon={HousingFormDefIcon}
            activeIcon={HousingFormActiveIcon}
            label={translations.housingForms}
            value={tabs.housingForms}
            href={pages.housingForms}
            onSelectTab={() => changeTab(pages.housingForms)}
          />
        </HeaderTabs.Group>
        <SearchBar
          placeholder={placeholder}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchBarInput={searchBarInput}
          setSearchBarInput={setSearchBarInput}
          currentView={view}
          onSwitchView={hasMapAndFilters ? toggleValue : undefined}
          onFiltersOpen={hasMapAndFilters ? onFiltersToggle : undefined}
          suggestionsFetcher={suggestionsFetcher}
        />
      </Column>
      <MemorizedDiscoverTabWrapper
        tab={tab}
        view={view}
        searchTerm={searchTerm}
        onResetSearch={onResetSearch}
        filtersOpen={filtersOpen}
        onFiltersClose={onFiltersClose}
      />
    </PageLayout>
  )
}

const DiscoverTabWrapper = ({
  tab,
  view,
  searchTerm,
  onResetSearch,
  filtersOpen,
  onFiltersClose,
}: {
  tab: Tabs
  view: 'grid' | 'map'
  searchTerm: string
  onResetSearch: () => void
  filtersOpen: boolean
  onFiltersClose: () => void
}) => {
  return (
    <Column alignItems="stretch" gap="2rem">
      {match(tab)
        .with(tabs.initiatives, () => (
          <DiscoverInitiatives
            view={view}
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
            filtersOpen={filtersOpen}
            onFiltersClose={onFiltersClose}
          />
        ))
        .with(tabs.locations, () => (
          <DiscoverPlots
            view={view}
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
            filtersOpen={filtersOpen}
            onFiltersClose={onFiltersClose}
          />
        ))
        .with(tabs.regions, () => (
          <DiscoverRegions
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
          />
        ))
        .with(tabs.housingForms, () => (
          <DiscoverHousingForms
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
          />
        ))
        .exhaustive()}
    </Column>
  )
}
const MemorizedDiscoverTabWrapper = memo(DiscoverTabWrapper)
